<template>
  <div class="bg-white">
    <!-- Hero Section -->
    <section class="bg-white py-5" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <CRow class="py-5">
          <CCol lg="10" xl="9" class="mx-auto text-center">
            <h1 class="display-3 font-weight-bold marketing-heading mb-4" style="font-size: 3.5rem; line-height: 1.1;">
              Save 14 Hours Monthly on Accounting & Payroll<br>
              <span class="text-marketing-primary">While Staying 100% Malaysian-Compliant</span>
            </h1>
            <p class="lead marketing-body mb-4" style="font-size: 1.25rem; max-width: 800px; margin-left: auto; margin-right: auto;">
              The only accounting software built specifically for Malaysian SMEs that automatically handles EPF, SOCSO, EIS, and SST calculations – trusted by 500+ local businesses to eliminate manual errors and late filings.
            </p>

            <!-- CTA Buttons -->
            <div class="d-flex flex-column flex-sm-row justify-content-center mb-4">
              <CButton
                class="btn-marketing-accent mr-sm-3 mb-3 mb-sm-0 px-5 py-3 font-weight-600 btn-scale"
                size="lg"
                to="/signup"
                style="font-size: 1.125rem;"
              >
                Start Free 30-Day Trial
              </CButton>
              <CButton
                size="lg"
                class="px-5 py-3 font-weight-600 btn-scale"
                to="/calculators"
                style="font-size: 1.125rem; border: 2px solid #334155; background: white; color: #334155;"
              >
                Try Free Calculators
              </CButton>
            </div>

            <p class="text-marketing-gray-500 small mb-4">
              No credit card required • 30-day free trial • Cancel anytime
            </p>

            <!-- Trust Badges -->
            <div class="d-flex flex-wrap justify-content-center align-items-center mt-5" style="gap: 1rem;">
              <span class="trust-badge">
                <CIcon name="cil-shield-alt" class="mr-2 text-marketing-accent"/>
                Bank Negara Compliant
              </span>
              <span class="trust-badge">
                <CIcon name="cil-check-circle" class="mr-2 text-marketing-accent"/>
                LHDN Approved
              </span>
              <span class="trust-badge">
                <CIcon name="cil-lock-locked" class="mr-2 text-marketing-accent"/>
                ISO 27001 Certified
              </span>
              <span class="trust-badge">
                <CIcon name="cil-people" class="mr-2 text-marketing-accent"/>
                SME Corp Partner
              </span>
            </div>
          </CCol>
        </CRow>

        <!-- Trust Indicators -->
        <CRow class="text-center py-5 mt-4">
          <CCol md="3" sm="6" class="mb-4 mb-md-0">
            <div class="h2 font-weight-bold text-marketing-primary mb-1">500+</div>
            <div class="text-marketing-gray-600 font-weight-500">Active Malaysian Businesses</div>
          </CCol>
          <CCol md="3" sm="6" class="mb-4 mb-md-0">
            <div class="h2 font-weight-bold text-marketing-primary mb-1">RM 50M+</div>
            <div class="text-marketing-gray-600 font-weight-500">Payroll Processed Error-Free</div>
          </CCol>
          <CCol md="3" sm="6" class="mb-4 mb-md-0">
            <div class="h2 font-weight-bold text-marketing-primary mb-1">14 Hours</div>
            <div class="text-marketing-gray-600 font-weight-500">Average Time Saved Monthly</div>
          </CCol>
          <CCol md="3" sm="6">
            <div class="h2 font-weight-bold text-marketing-primary mb-1">4.8/5</div>
            <div class="text-marketing-gray-600 font-weight-500">200+ Verified Reviews</div>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Key Features -->
    <section class="py-5 bg-marketing-light">
      <CContainer class="py-5">
        <CRow class="mb-5">
          <CCol lg="8" class="mx-auto text-center">
            <h2 class="display-5 font-weight-bold marketing-heading mb-3">
              Everything Malaysian SMEs Need to Thrive
            </h2>
            <p class="lead marketing-body">
              Built by Malaysians, for Malaysian businesses – with local compliance at the core
            </p>
          </CCol>
        </CRow>

        <CRow>
          <!-- Feature 1 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-check-circle" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Never Miss Another EPF Deadline</h5>
                <p class="marketing-body mb-0">
                  Automatic EPF, SOCSO, and EIS calculations with built-in filing reminders. Zero compliance errors, guaranteed.
                </p>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Feature 2 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-file" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Get Paid 2X Faster</h5>
                <p class="marketing-body mb-0">
                  Professional invoices with SST auto-calculation. Automated payment reminders reduce average collection time from 30 to 14 days.
                </p>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Feature 3 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-chart-line" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Know Your Profit in Real-Time</h5>
                <p class="marketing-body mb-0">
                  Live profit & loss, balance sheet, and cash flow dashboards. Make data-driven decisions, not gut feelings.
                </p>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Feature 4 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-wallet" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Cut Business Costs by 15%</h5>
                <p class="marketing-body mb-0">
                  Smart expense tracking with AI categorization finds wasteful spending. Average customer saves RM 2,500/month.
                </p>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Feature 5 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-basket" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Never Run Out of Stock Again</h5>
                <p class="marketing-body mb-0">
                  Smart inventory alerts predict restocking needs 2 weeks in advance. No more lost sales or excess inventory.
                </p>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Feature 6 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-people" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Secure Team Collaboration</h5>
                <p class="marketing-body mb-0">
                  Role-based access control keeps sensitive data safe. Your accountant, bookkeeper, and staff each see only what they need.
                </p>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Free Calculators -->
    <section class="bg-white py-5">
      <CContainer class="py-5">
        <CRow class="mb-5">
          <CCol lg="8" class="mx-auto text-center">
            <h2 class="display-5 font-weight-bold marketing-heading mb-3">
              Free Malaysian Compliance Calculators
            </h2>
            <p class="lead marketing-body">
              73% of businesses switch after using our calculators – see why
            </p>
          </CCol>
        </CRow>

        <CRow>
          <!-- EPF Calculator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-calculator" size="3xl" class="text-primary mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">EPF Calculator</h5>
                <p class="text-muted small mb-3 text-dark">Calculate employee & employer EPF contributions</p>
                <CButton class="btn-marketing-outline-primary" size="sm" block to="/calculators/epf">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- SOCSO Calculator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-calculator" size="3xl" class="text-success mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">SOCSO Calculator</h5>
                <p class="text-muted small mb-3 text-dark">Calculate SOCSO contributions based on wage brackets</p>
                <CButton class="btn-marketing-outline-success" size="sm" block to="/calculators/socso">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- SST Calculator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-calculator" size="3xl" class="text-warning mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">SST Calculator</h5>
                <p class="text-muted small mb-3 text-dark">Calculate Sales & Service Tax (6% or 10%)</p>
                <CButton class="btn-marketing-outline-warning" size="sm" block to="/calculators/sst">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Invoice Generator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-file" size="3xl" class="text-info mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">Invoice Generator</h5>
                <p class="text-muted small mb-3 text-dark">Create professional invoices instantly</p>
                <CButton class="btn-marketing-outline-info" size="sm" block to="/calculators/invoice">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Profit Margin Calculator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-chart-line" size="3xl" class="text-success mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">Profit Margin</h5>
                <p class="text-muted small mb-3 text-dark">Calculate gross & net profit margins</p>
                <CButton class="btn-marketing-outline-success" size="sm" block to="/calculators/profit-margin">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Break-Even Calculator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-chart-pie" size="3xl" class="text-danger mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">Break-Even</h5>
                <p class="text-muted small mb-3 text-dark">Find your break-even point</p>
                <CButton class="btn-marketing-outline-danger" size="sm" block to="/calculators/break-even">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Take-Home Salary Calculator -->
          <CCol lg="3" md="6" class="mb-4">
          <!-- Generic Replace for Simple Cards -->
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="text-center p-4">
                <CIcon name="cil-money" size="3xl" class="text-warning mb-3"/>
                <h5 class="font-weight-bold mb-2 text-dark">Take-Home Salary</h5>
                <p class="text-muted small mb-3 text-dark">Calculate net salary after deductions</p>
                <CButton class="btn-marketing-outline-warning" size="sm" block to="/calculators/salary">
                  Try Calculator
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- View All -->
          <CCol lg="3" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm hover-lift text-white" style="background: linear-gradient(135deg, #1E3A5F 0%, #2A4D7C 100%);">
              <CCardBody class="text-center p-4 d-flex flex-column justify-content-center">
                <CIcon name="cil-grid" size="3xl" class="mb-3"/>
                <h5 class="font-weight-bold mb-2">View All Calculators</h5>
                <p class="small mb-0">Explore all free tools</p>
                <CButton size="sm" block to="/calculators" class="mt-3 btn-marketing-accent">
                  View All
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Pricing Preview -->
    <section class="py-5">
      <CContainer class="py-5">
        <CRow class="mb-5">
          <CCol lg="8" class="mx-auto text-center">
            <h2 class="display-5 font-weight-bold mb-3">
              Simple, Transparent Pricing
            </h2>
            <p class="lead text-muted">
              Choose the plan that's right for your business
            </p>
          </CCol>
        </CRow>

        <CRow>
          <!-- Starter Plan -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow hover-lift">
              <CCardBody class="p-4">
                <div class="text-center mb-4">
                  <h4 class="font-weight-bold text-dark">Starter</h4>
                  <div class="my-3">
                    <span class="h2 font-weight-bold text-dark">RM 99</span>
                    <span class="text-muted">/month</span>
                  </div>
                  <p class="text-muted">Perfect for small businesses</p>
                  <CButton class="btn-marketing-accent btn-scale" block to="/signup?plan=starter">
                    Get Started
                  </CButton>
                </div>
                <ul class="list-unstyled text-dark">
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Up to 5 employees
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Unlimited invoices
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Basic reports
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Email support
                  </li>
                </ul>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Professional Plan (Popular) -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-primary shadow-lg hover-lift">
              <CCardHeader class="bg-primary text-white text-center py-2">
                <strong>MOST POPULAR</strong>
              </CCardHeader>
              <CCardBody class="p-4">
                <div class="text-center mb-4">
                  <h4 class="font-weight-bold text-dark">Professional</h4>
                  <div class="my-3">
                    <span class="h2 font-weight-bold text-dark">RM 199</span>
                    <span class="text-muted">/month</span>
                  </div>
                  <p class="text-muted">Best for growing businesses</p>
                  <CButton class="btn-marketing-accent btn-scale" block to="/signup?plan=professional">
                    Get Started
                  </CButton>
                </div>
                <ul class="list-unstyled text-dark">
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Up to 20 employees
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Unlimited everything
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Advanced reports
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Priority support
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Multi-user access (5 users)
                  </li>
                </ul>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Enterprise Plan -->
          <CCol lg="4" md="12" class="mb-4">
            <CCard class="h-100 border-0 shadow hover-lift">
              <CCardBody class="p-4">
                <div class="text-center mb-4">
                  <h4 class="font-weight-bold text-dark">Enterprise</h4>
                  <div class="my-3">
                    <span class="h2 font-weight-bold text-dark">Custom</span>
                  </div>
                  <p class="text-muted">For large organizations</p>
                  <CButton class="btn-outline-light-custom btn-scale" style="border-color: var(--marketing-primary); color: var(--marketing-primary); background: white;" block to="/contact?plan=enterprise">
                    Contact Sales
                  </CButton>
                </div>
                <ul class="list-unstyled text-dark">
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Unlimited employees
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Custom integrations
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Dedicated support
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    Custom training
                  </li>
                  <li class="mb-2">
                    <CIcon name="cil-check-circle" class="text-marketing-accent mr-2"/>
                    SLA guarantee
                  </li>
                </ul>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Testimonials -->
    <section class="bg-light py-5">
      <CContainer class="py-5">
        <CRow class="mb-5">
          <CCol lg="8" class="mx-auto text-center">
            <h2 class="display-5 font-weight-bold mb-3">
              Trusted by Malaysian Businesses
            </h2>
            <p class="lead text-muted">
              See what our customers have to say
            </p>
          </CCol>
        </CRow>

        <CRow>
          <!-- Testimonial 1 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="p-4">
                <div class="mb-3 text-warning">
                  <CIcon name="cil-star" v-for="i in 5" :key="i"/>
                </div>
                <p class="mb-4">
                  "Niagaku has transformed how we manage payroll. The EPF/SOCSO calculations are automatic and always accurate. Highly recommended!"
                </p>
                <div class="d-flex align-items-center">
                  <div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-3" style="width: 50px; height: 50px;">
                    <strong>AH</strong>
                  </div>
                  <div>
                    <div class="font-weight-bold">Ahmad Hassan</div>
                    <div class="text-muted small">Hassan Trading Sdn Bhd</div>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Testimonial 2 -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="p-4">
                <div class="mb-3 text-warning">
                  <CIcon name="cil-star" v-for="i in 5" :key="i"/>
                </div>
                <p class="mb-4">
                  "The invoicing feature saves us hours every week. Professional templates and automatic payment reminders keep our cash flow healthy."
                </p>
                <div class="d-flex align-items-center">
                  <div class="bg-success text-white rounded-circle d-flex align-items-center justify-content-center mr-3" style="width: 50px; height: 50px;">
                    <strong>SL</strong>
                  </div>
                  <div>
                    <div class="font-weight-bold">Sarah Lim</div>
                    <div class="text-muted small">Lim Consultancy</div>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Testimonial 3 -->
          <CCol lg="4" md="12" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm hover-lift">
              <CCardBody class="p-4">
                <div class="mb-3 text-warning">
                  <CIcon name="cil-star" v-for="i in 5" :key="i"/>
                </div>
                <p class="mb-4">
                  "Real-time financial reports give us clear insights into our business performance. Making data-driven decisions has never been easier."
                </p>
                <div class="d-flex align-items-center">
                  <div class="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center mr-3" style="width: 50px; height: 50px;">
                    <strong>RK</strong>
                  </div>
                  <div>
                    <div class="font-weight-bold">Raj Kumar</div>
                    <div class="text-muted small">TechStart Solutions</div>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Final CTA -->
    <section class="bg-marketing-dark text-white py-5" style="background: linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%);">
      <CContainer class="py-5">
        <CRow>
          <CCol lg="9" class="mx-auto text-center">
            <h2 class="display-4 font-weight-bold mb-4" style="color: white;">
              Ready to Save 14 Hours Monthly?
            </h2>
            <p class="lead mb-4" style="color: #CBD5E1; font-size: 1.25rem;">
              Join 500+ Malaysian businesses who eliminated manual errors and never miss another compliance deadline. Start your risk-free trial today.
            </p>
            <div class="d-flex flex-column flex-sm-row justify-content-center mb-4">
              <CButton
                class="btn-marketing-accent mr-sm-3 mb-3 mb-sm-0 px-5 py-3 font-weight-600 btn-scale"
                size="lg"
                to="/signup"
                style="font-size: 1.125rem;"
              >
                Start Free 30-Day Trial
              </CButton>
              <CButton
                size="lg"
                class="px-5 py-3 font-weight-600 btn-outline-light-custom btn-scale"
                to="/contact"
                style="font-size: 1.125rem;"
              >
                Schedule Demo
              </CButton>
            </div>
            <p class="small" style="color: #94A3B8;">
              No credit card required • 30-day money-back guarantee • Free data migration
            </p>

            <!-- Additional trust signals -->
            <div class="mt-5 pt-4" style="border-top: 1px solid rgba(255, 255, 255, 0.1);">
              <p class="text-uppercase font-weight-600 mb-3" style="color: #94A3B8; font-size: 0.75rem; letter-spacing: 1px;">
                Trusted By Leading Malaysian Businesses
              </p>
              <div class="d-flex flex-wrap justify-content-center align-items-center" style="gap: 2rem;">
                <span style="color: #CBD5E1; font-weight: 500;">F&B Chains</span>
                <span style="color: #CBD5E1;">•</span>
                <span style="color: #CBD5E1; font-weight: 500;">Retail Stores</span>
                <span style="color: #CBD5E1;">•</span>
                <span style="color: #CBD5E1; font-weight: 500;">Professional Services</span>
                <span style="color: #CBD5E1;">•</span>
                <span style="color: #CBD5E1; font-weight: 500;">Construction Firms</span>
              </div>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Homepage',
  metaInfo: {
    title: 'Niagaku - Accounting & Payroll Software for Malaysian SMEs',
    meta: [
      {
        name: 'description',
        content: 'Automate invoicing, payroll, EPF/SOCSO calculations. Free calculators & tools. Trusted by 500+ Malaysian businesses. Start free trial.'
      }
    ]
  }
}
</script>

<style scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.shadow-lg {
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
}

.h-100 {
  height: 100% !important;
}
</style>